import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Clock, Github, Upload, Loader2, AlertCircle, Shield, Eye, FileCheck, Activity } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { useUserScans, UserScan } from '../hooks/useUserScans.ts';
import { useSession } from '../hooks/useSession.ts';

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Get scan source icon and label
function getScanSourceInfo(sourceType: string) {
  switch (sourceType.toLowerCase()) {
    case 'github':
      return { icon: Github, label: 'GitHub', color: 'text-blue-400' };
    case 'upload':
      return { icon: Upload, label: 'Upload', color: 'text-green-400' };
    case 'demo':
      return { icon: Activity, label: 'Demo', color: 'text-purple-400' };
    default:
      return { icon: Upload, label: 'Upload', color: 'text-gray-400' };
  }
}

// Get status badge styling
function getStatusBadge(status: string) {
  switch (status.toLowerCase()) {
    case 'completed':
      return { text: 'Completed', className: 'bg-green-900/20 text-green-400 border-green-700/50' };
    case 'processing':
      return { text: 'Processing', className: 'bg-yellow-900/20 text-yellow-400 border-yellow-700/50' };
    case 'pending':
      return { text: 'Pending', className: 'bg-blue-900/20 text-blue-400 border-blue-700/50' };
    case 'failed':
      return { text: 'Failed', className: 'bg-red-900/20 text-red-400 border-red-700/50' };
    default:
      return { text: status, className: 'bg-gray-900/20 text-gray-400 border-gray-700/50' };
  }
}

// Scan card component
function ScanCard({ scan }: { scan: UserScan }) {
  const sourceInfo = getScanSourceInfo(scan.source_type);
  const statusBadge = getStatusBadge(scan.status);
  const SourceIcon = sourceInfo.icon;

  console.log('[ScanCard] Rendering scan:', { 
    id: scan.id, 
    status: scan.status, 
    findings: scan.findings_count,
    isCompleted: scan.status === 'completed',
    showViewResults: scan.status === 'completed'
  });

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-gray-700 ${sourceInfo.color}`}>
            <SourceIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Scan on {formatDate(scan.created_at)}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>Source: {sourceInfo.label}</span>
              {scan.findings_count !== undefined && (
                <>
                  <span>•</span>
                  <span>{scan.findings_count} issue{scan.findings_count !== 1 ? 's' : ''} found</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${statusBadge.className}`}>
          {statusBadge.text}
        </div>
      </div>



      <div className="flex justify-end">
        {scan.status === 'completed' ? (
          <Link
            to={`/results/${scan.id}`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View Results
          </Link>
        ) : (
          <div className="px-4 py-2 bg-gray-700 text-gray-400 rounded-lg cursor-not-allowed font-medium">
            {scan.status === 'processing' ? 'Processing...' : 'View Results'}
          </div>
        )}
      </div>
    </div>
  );
}

// Empty state component
function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="flex justify-center mb-6">
        <div className="p-4 bg-gray-800 rounded-full">
          <Search className="w-12 h-12 text-gray-400" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">🔍 No scans yet</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        You haven't run any scans yet. Upload your code or connect a GitHub repository to get started with security analysis.
      </p>
      <Link
        to="/upload"
        className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
      >
        <Upload className="w-4 h-4" />
        Run your first scan
      </Link>
    </div>
  );
}

// Loading state component
function LoadingState() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-800 border border-gray-700 rounded-lg p-6 animate-pulse">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-700 rounded-lg"></div>
              <div>
                <div className="w-48 h-5 bg-gray-700 rounded mb-2"></div>
                <div className="w-32 h-4 bg-gray-700 rounded"></div>
              </div>
            </div>
            <div className="w-20 h-6 bg-gray-700 rounded-full"></div>
          </div>
          <div className="flex justify-end">
            <div className="w-24 h-10 bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Error state component
function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="flex justify-center mb-6">
        <div className="p-4 bg-red-900/20 rounded-full">
          <AlertCircle className="w-12 h-12 text-red-400" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Error Loading Scans</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">{error}</p>
      <button
        onClick={onRetry}
        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

export default function YourScansScreen() {
  const navigate = useNavigate();
  const { session, loading: sessionLoading } = useSession();
  const { scans, loading, error, refetch } = useUserScans();

  console.log('[YourScansScreen] Rendering with state:', {
    sessionLoading,
    hasSession: !!session,
    scansCount: scans.length,
    loading,
    error
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  // Redirect to login if not authenticated
  if (!sessionLoading && !session) {
    console.log('[YourScansScreen] No session, redirecting to login');
    navigate('/login');
    return null;
  }

  // Show loading state while session is loading
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-gray-700 rounded-md w-48"></div>
              <LoadingState />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 font-sans flex flex-col">
      <Navbar />
      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center mb-8">
            <button
              onClick={handleGoBack}
              className="flex items-center text-gray-400 hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <h1 className="text-3xl font-bold text-white">Your Scans</h1>
          </div>

          {/* Content */}
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState error={error} onRetry={refetch} />
          ) : scans.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-6">
              {scans.map((scan) => (
                <ScanCard key={scan.id} scan={scan} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
} 